import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';

import { isAdyenRefusalError } from '@/errorCodes/adyen';
import type {
  IncomingFormattedError,
  OutgoingFormattedError,
} from '@/errorCodes/common';
import { mapApiErrorCode } from '@/errorCodes/helpers';
import type { Locale } from '@/types/regionTypes';
import { getActionAriaLabelSync } from '@/utils/ariaLabels';

import { Typography } from '@/components/Typography';
import { useContactInfo } from '@/hooks/useContactInfo';
import type { ApiErrorType, ApiErrorWithType } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { WarrantyStatusEnum } from '@/views/MyWatchView/types';

import * as S from './ServerErrors.styles';
export interface ServerErrorsProps {
  [key: string]: unknown;
  callCustomerService?: () => void;
  errors: ApiErrorWithType[];
  isFinancingAvailable?: boolean;
  locale?: Locale;
  setErrors?: (errors: ApiErrorWithType[]) => void;
  type?: string;
}

export const getFormattedMessage = (
  error: ApiErrorWithType,
  formatMessage: IntlShape['formatMessage'],
  mapApiErrorCode: (
    type: ApiErrorType,
    error: IncomingFormattedError,
    isFinancingAvailable?: boolean,
    phoneNumber?: string,
  ) => OutgoingFormattedError,
  isFinancingAvailable?: boolean,
  phoneNumber?: string,
) => {
  const mappedMessage = mapApiErrorCode(
    error.type,
    error,
    isFinancingAvailable,
    phoneNumber,
  );

  const message =
    typeof mappedMessage?.message === 'object'
      ? formatMessage(mappedMessage.message, {
          message: error.message || '',
          ...mappedMessage.values,
        })
      : mappedMessage?.message;

  return message;
};

const IncompleteRegistrationNotice = ({
  errorCode,
}: {
  errorCode: WarrantyStatusEnum.RegisteredByOther | WarrantyStatusEnum.NotFound;
}) => {
  const errorKeyMap = {
    [WarrantyStatusEnum.NotFound]: 'notActivated',
    [WarrantyStatusEnum.RegisteredByOther]: 'registeredByOthers',
  };

  const errorKey = errorKeyMap[errorCode];

  const getMessageId = (suffix: string) =>
    `addWatch.registration.incomplete.${suffix}`;

  return (
    <S.IncompleteRegistrationNotice>
      {errorCode === WarrantyStatusEnum.NotFound && (
        <Typography>
          <FormattedMessage
            id={getMessageId(`${errorKey}Intro`)}
            values={{ br: <br /> }}
          />
        </Typography>
      )}

      <Typography>
        <FormattedMessage
          id={getMessageId(`${errorKey}Note`)}
          values={{ br: <br /> }}
        />
      </Typography>
    </S.IncompleteRegistrationNotice>
  );
};

export const ServerErrors = ({
  errors,
  type = 'danger',
  ...props
}: ServerErrorsProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const intl = useIntl();
  const contactInfo = useContactInfo();
  const { locale } = useRegionProvider();

  useEffect(() => {
    setIsVisible(true);
  }, [errors]);

  if (!isVisible || errors.length === 0) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    props.setErrors?.([]);
  };

  return (
    <S.Wrapper data-test-id='ServerErrorsWrapper' my={2} type={type} {...props}>
      {errors.map((error, i) => {
        if (
          (error.field === WarrantyStatusEnum.RegisteredByOther?.toString() ||
            error.field === WarrantyStatusEnum.NotFound?.toString()) &&
          error.type === 'addWatch'
        ) {
          return (
            <IncompleteRegistrationNotice
              key={error.code}
              errorCode={parseInt(error.field)}
            />
          );
        }
        const message = getFormattedMessage(
          error,
          intl.formatMessage,
          mapApiErrorCode,
          props.isFinancingAvailable,
          contactInfo.phoneNumber,
        );

        if (!message) {
          return null;
        }

        if (
          error.type === 'adyen' &&
          isAdyenRefusalError(error.code, error.message as string)
        ) {
          return (
            <Typography
              key={error.code + i}
              as='span'
              scale='p3'
              style={{ lineHeight: 2, display: 'inline-block' }}
              textAlign='left'
              width='100%'
            >
              {message}
            </Typography>
          );
        }
        return (
          <Typography
            key={error.code}
            data-test-id={`ServerErrorsTypography-${i}`}
            scale='p3'
          >
            {message}
          </Typography>
        );
      })}
      <S.CloseButton
        aria-label={
          getActionAriaLabelSync('dismiss_error', locale.code) ||
          'Dismiss error'
        }
        data-test-id='ServerErrorsCloseButton'
        onClick={() => handleClose()}
        type='button'
      >
        <S.CloseIcon aria-hidden='true' />
      </S.CloseButton>
    </S.Wrapper>
  );
};

export const CustomErrors = ({ message }: { message: ReactNode }) => {
  return message ? (
    <S.CustomWrapper data-test-id='ServerErrorsWrapper' my={2}>
      <S.Badge data-test-id='infoIcon' />
      <Typography scale='p3'>{message}</Typography>
    </S.CustomWrapper>
  ) : null;
};
